import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchStationMetrics } from '@/lib/api';
import type { StationMetricsTableData } from '@/types/reports';

interface UseStationMetricsOptions {
  groupIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  modelCodes?: string[];
  industryTypes?: string[];
  /** Only these units (Reports → Unit Status click-to-focus); null/absent = no unit filter */
  unitIds?: string[] | null;
  enabled?: boolean;
  includeAllDeployments?: boolean;
}

interface UseStationMetricsResult {
  data: StationMetricsTableData | null;
  isLoading: boolean;
  error: Error | null;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refetch: () => void;
}

/**
 * Hook for fetching station energy metrics from Supabase
 * Used by Reports page to display real energy data from unit_energy_metrics table
 *
 * Implements request cancellation to prevent:
 * - setState on unmounted components
 * - Stale responses from superseded requests
 */
export function useStationMetrics({
  groupIds,
  dateRange,
  modelCodes,
  industryTypes,
  unitIds = null,
  enabled = true,
  includeAllDeployments = false,
}: UseStationMetricsOptions): UseStationMetricsResult {
  const [data, setData] = useState<StationMetricsTableData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Track previous filter values to detect changes and reset page
  const prevFiltersRef = useRef<{
    groupIds: string[];
    dateRange: typeof dateRange;
    modelCodes?: string[];
    industryTypes?: string[];
    unitIds?: string[] | null;
    includeAllDeployments?: boolean;
  }>({
    groupIds: [],
    dateRange: { start: null, end: null },
    modelCodes: undefined,
    industryTypes: undefined,
    unitIds: null,
    includeAllDeployments: false,
  });

  // Track mount state and current request for cancellation
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Serialize dependencies for stable comparison to avoid unnecessary re-fetches
  const groupIdsKey = useMemo(() => groupIds.join(','), [groupIds]);
  const dateRangeKey = useMemo(
    () => `${dateRange.start?.getTime() ?? ''}-${dateRange.end?.getTime() ?? ''}`,
    [dateRange.start, dateRange.end]
  );
  const modelCodesKey = useMemo(() => modelCodes?.join(',') ?? '', [modelCodes]);
  const industryTypesKey = useMemo(() => industryTypes?.join(',') ?? '', [industryTypes]);
  const includeAllDeploymentsKey = useMemo(() => String(includeAllDeployments), [includeAllDeployments]);
  const unitIdsKey = useMemo(() => unitIds?.join(',') ?? '', [unitIds]);

  const fetchData = useCallback(async (forcePage?: number) => {
    // Don't fetch if disabled or no groups selected
    if (!enabled || groupIds.length === 0) {
      setData(null);
      return;
    }

    // Don't fetch if date range is incomplete
    if (!dateRange.start || !dateRange.end) {
      setData(null);
      return;
    }

    // Cancel any in-flight request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Check if filters changed - if so, reset to page 1
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current.groupIds) !== JSON.stringify(groupIds) ||
      prevFiltersRef.current.dateRange.start?.getTime() !== dateRange.start?.getTime() ||
      prevFiltersRef.current.dateRange.end?.getTime() !== dateRange.end?.getTime() ||
      JSON.stringify(prevFiltersRef.current.modelCodes) !== JSON.stringify(modelCodes) ||
      JSON.stringify(prevFiltersRef.current.industryTypes) !== JSON.stringify(industryTypes) ||
      (prevFiltersRef.current.unitIds?.join(',') ?? '') !== unitIdsKey ||
      prevFiltersRef.current.includeAllDeployments !== includeAllDeployments;

    // Update ref with current values
    prevFiltersRef.current = { groupIds, dateRange, modelCodes, industryTypes, unitIds, includeAllDeployments };

    // Determine which page to use
    let effectivePage = forcePage ?? page;
    if (filtersChanged && forcePage === undefined) {
      effectivePage = 1;
      setPage(1);
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchStationMetrics(
        groupIds,
        dateRange,
        effectivePage,
        pageSize,
        abortController.signal,
        modelCodes,
        industryTypes,
        includeAllDeployments,
        unitIds
      );

      // Only update state if request wasn't aborted and component is still mounted
      if (!abortController.signal.aborted && isMountedRef.current) {
        setData(result);
        setIsLoading(false);
      }
    } catch (err) {
      // Ignore AbortError - these are expected when cancelling requests
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        console.error('[useStationMetrics] Failed to fetch:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch station metrics'));
        setData(null);
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Using serialized keys for stable dependencies (groupIds/dateRange/modelCodes/industryTypes/includeAllDeployments captured by their keys)
  }, [enabled, groupIdsKey, dateRangeKey, modelCodesKey, industryTypesKey, unitIdsKey, includeAllDeploymentsKey, page, pageSize]);

  // Fetch on mount and when dependencies change
  // Cleanup aborts in-flight requests on unmount or dependency change
  useEffect(() => {
    isMountedRef.current = true;
    fetchData();

    return () => {
      isMountedRef.current = false;
      // Abort any in-flight request on cleanup
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
    setPage(1); // Reset to first page when changing page size
  }, []);

  return {
    data,
    isLoading,
    error,
    page,
    pageSize,
    setPage: handleSetPage,
    setPageSize: handleSetPageSize,
    refetch: fetchData,
  };
}
